import { useEffect, useRef, useState, useCallback, ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { USER } from "@types/user";
import { USERTENANT } from "@types/usertenant";
import { ROLE } from "@types/role";
import { TENANTDEPARTMENT } from "@types/tenantDepartment";
import { BROADCASTGROUP } from "@types/broadcastGroup";
import { APP } from "@types/app";

import BlankUser from "@templates/BlankUserData";
import { BlankUserTenantData } from "@templates/BlankUserTenantData";
import { BlankRoleData } from "@templates/BlankRoleData";
import { BlankTenantDepartmentData } from "@templates/BlankTenantDepartmentData";
import { BlankBroadcastGroupData } from "@templates/BlankBroadcastGroupData";

import {
  getUserData,
  postUserData,
  putUserData,
  resetUserPassword,
  uploadUserImage,
  deleteUserImage,
} from "@services/API/Requests/UserRequests";
import { getAppData } from "@services/API/Requests/AppRequests";
import { getRoleData } from "@services/API/Requests/RoleRequests";
import { USER_TYPE } from "@constants/Enums";

import {
  clearUserFormData,
  clearUserFormImageData,
  setFilesData,
  setImagePreviewData,
  setSelectedAppsData,
  setSelectedRoleData,
  setTempUserId,
  setTenantDepartmentFormData,
  setUserFormData,
  setUserTenantDepartmentData,
} from "@services/StateManagement/UserFormSlice";

import { TranslateErrorMessage } from "@services/ErrorHandling";

import { error_message, UserFieldErrorMessages } from "@constants/Errors";
import { APP_NAMES } from "@constants/Enums";
import { ROLES } from "@constants/Enums";
import { postContact } from "@services/API/Requests/ContactRequests";
import { translatedDuplicateMessages } from "@constants/Errors";
import { setBroadcastGroupData } from "../../../../services/StateManagement/UserFormSlice";

export const useUserProfileForm = (id: string) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initUserData = useSelector((state) => state.userForm.userData);
  const tempUserId = useSelector((state) => state.userForm.tempUserId);

  const [createUser] = useState(id === "createUser" ? true : false);

  const [myUserPage] = useState(id === "myPage" ? true : false);
  const availableApps = useSelector((state) => state.apps.availableApps);

  const [hasYobelyLicense, setHasYobelyLicense] = useState<boolean>(false);
  const [hasKakelyLicense, setHasKakelyLicense] = useState<boolean>(false);
  const [yobelyLicenseError, setYobelyLicenseError] = useState<boolean>(false);

  const initTenantDepartmentData = useSelector(
    (state) => state.userForm.tenantDepartmentData,
  );
  const initUserTenantData = useSelector(
    (state) => state.userForm.userTenantData,
  );
  const initSelectedAppsData = useSelector(
    (state) => state.userForm.selectedAppsData,
  );
  const initRoleData = useSelector((state) => state.userForm.selectedRoleData);
  const initImagePreviewData = useSelector(
    (state) => state.userForm.imagePreviewData,
  );
  const initFilesData = useSelector((state) => state.userForm.filesData);
  const initBroadcastGroupData = useSelector(
    (state) => state.userForm.broadcastGroupData,
  );
  const [readFromCache, setReadFromCache] = useState<boolean>(false);

  const [userData, setUser] = useState<USER | undefined>();
  const [userTenantData, setUserTenantData] = useState<
    USERTENANT | undefined
  >();

  const [tenantDepartmentData, setTenantDepartmentData] = useState<
    TENANTDEPARTMENT | undefined
  >();
  const [roles, setRoles] = useState<ROLE[]>([]);
  const [userRole, setUserRole] = useState<ROLE | undefined>();
  const [currentUserRole, setCurrentUserRole] = useState<ROLE | undefined>();
  const [adminRoleSelected, setAdminRoleSelected] = useState<boolean>(false);
  const [contactRoleSelected, setContactRoleSelected] =
    useState<boolean>(false);

  const isAdminLoggedIn: boolean = useSelector((state) => state.auth.isAdmin);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [roleError, setRoleError] = useState<string>("");
  const [userNotFound, setUserNotFound] = useState<boolean>(false);

  const [imageLoading, setImageLoading] = useState<boolean>(true);

  const [broadcastGroup, setBroadcastGroup] = useState<
    BROADCASTGROUP | undefined
  >();

  const fileInput: React.MutableRefObject<null> = useRef(null);

  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [imageChange, setImageChanged] = useState<boolean>(false);
  const [doImageDelete, setDoImageDelete] = useState<boolean>(false);

  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [apps, setApps] = useState<APP[]>([]);
  const [userApps, setUserApps] = useState<APP[]>([]);
  const [selectedApps, setSelectedApps] = useState<APP[]>([]);
  const [keycloakGroups, setKeycloakGroups] = useState<never[]>([]);

  const errorToast = (response) => toast.error(`${response}`);
  const successToast = (response) => toast.success(`${response}`);

  const [duplicateExistsModal, setDuplicateExistsModal] =
    useState<boolean>(false);
  const [duplicateUsers, setDuplicateUsers] = useState<USER[]>([]);
  const [dupErrorType, setDupErrorType] = useState<string>("");

  const [departmentModalOpen, setDepartmentModalOpen] =
    useState<boolean>(false);
  const [passwordChangeModal, setPasswordChangeModalOpen] =
    useState<boolean>(false);
  const [broadcastGroupModalOpen, setBroadcastGroupModalOpen] =
    useState<boolean>(false);
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);

  const [departmentModalTitle, setDepartmentModalTitle] = useState<
    string | undefined
  >(undefined);

  const [errors, setErrors] = useState({
    name: false,
    first_name: false,
    last_name: false,
    first_kana_name: false,
    last_kana_name: false,
    furigana_name: false,
    first_furigana_name: false,
    last_furigana_name: false,
    personal_tel: false,
    employee_number: false,
    login_id: false,
    login_id_exists: false,
    login_id_length: false,
    email_exists: false,
    position_name: false,
    kana_name_length: false,
    furigana_name_length: false,
    password: false,
  });

  useEffect(() => {
    if (roles && roles.length > 0 && userRole) {
      const matchingRole = roles.find((role) => role.id === userRole.id);

      if (matchingRole) {
        setCurrentUserRole(matchingRole);
      } else {
        setCurrentUserRole(BlankRoleData);
      }
    } else if (isAdminLoggedIn) {
      const generalRole = roles.find((role) => role.id === 1);
      setCurrentUserRole(generalRole);
    }
  }, [roles, userRole, createUser]);

  useEffect(() => {
    if (hasYobelyLicense && !availableApps.includes(APP_NAMES.YOBELY)) {
      setYobelyLicenseError(true);
    } else {
      if (!hasYobelyLicense) {
        setYobelyLicenseError(false);
      }
    }
  }, [hasYobelyLicense]);

  useEffect(() => {
    const fetchUserData = async () => {
      await getUserData(
        setUser,
        setUserTenantData,
        setTenantDepartmentData,
        setUserRole,
        setUserApps,
        setKeycloakGroups,
        setBroadcastGroup,
        setLoading,
        setError,
        id,
        myUserPage,
        true,
      ).then((response) => {
        if (response?.status >= 300) {
          setUserNotFound(true);
        } else {
          if (userData === undefined) {
            if (id == tempUserId) {
              setReadFromCache(true);
              setUser(initUserData || BlankUser);
              setTenantDepartmentData(
                initTenantDepartmentData || BlankTenantDepartmentData,
              );
              setBroadcastGroup(
                initBroadcastGroupData || BlankBroadcastGroupData,
              );
              setUserTenantData(initUserTenantData || BlankUserTenantData);
              setUserApps(initSelectedAppsData || []);
              setUserRole(initRoleData || BlankRoleData);
              if (initImagePreviewData) {
                setPreviews(initImagePreviewData);
              }
              if (initUserData) {
                if (initFilesData.length > 0) {
                  setFiles(initFilesData);
                }
              } else {
                dispatch(clearUserFormImageData());
              }
              dispatch(clearUserFormData());
            }

            setUserNotFound(true);
          } else {
            setUserNotFound(false);
          }
        }
      });
    };

    const fetchRoleData = async () => {
      await getRoleData(setRoles, setError, setLoading);
    };

    const fetchAppData = async () => {
      await getAppData(setApps, setError, setLoading);
    };

    fetchRoleData();
    fetchAppData();

    if (!createUser) {
      fetchUserData();
    } else {
      if (tempUserId != "createUser") {
        setUser(BlankUser);
      } else {
        setUser(initUserData || BlankUser);
        setTenantDepartmentData(
          initTenantDepartmentData || BlankTenantDepartmentData,
        );
        setBroadcastGroup(BlankBroadcastGroupData);
        setUserTenantData(initUserTenantData || BlankUserTenantData);
        setUserApps(initSelectedAppsData || []);
        setCurrentUserRole(initRoleData || BlankRoleData);
        if (initImagePreviewData) {
          setPreviews(initImagePreviewData);
        }
        setFiles(initFilesData);
        dispatch(clearUserFormData());
      }
      setLoading(false);
      if (isAdminLoggedIn) {
        updateUserData("personal_tel", "00000000000");
      }
    }
  }, [id, createUser]);

  useEffect(() => {
    if (!readFromCache) {
      if (userData?.profile_image_url) {
        setPreviews([userData.profile_image_url]);
      }
    }
  }, [userData?.profile_image_url]);

  useEffect(() => {
    if (userData?.login_password) {
      setPassword(userData.login_password);
    }
  }, [userData?.login_password, password]);

  useEffect(() => {
    if (currentUserRole?.name?.includes(ROLES.ADMIN)) {
      setAdminRoleSelected(true);
    } else {
      setAdminRoleSelected(false);
    }

    if (currentUserRole?.name?.includes(ROLES.CONTACT)) {
      setContactRoleSelected(true);
    } else {
      setContactRoleSelected(false);
    }
  }, [currentUserRole]);

  const updatePassword = (value) => {
    setPassword(value);
    if (userData) {
      updateUserData("login_password", value);
    }
  };

  const updateUserData = (key: keyof USER, value: string | boolean) => {
    setUser((prevState) => {
      if (prevState) {
        if (
          key == "is_display" ||
          key == "user_type" ||
          key == "notes" ||
          key == "company_address"
        ) {
          return {
            ...prevState,
            [key]: value,
          };
        }
        return {
          ...prevState,
          [key]: value.trim(),
        };
      }
      return prevState;
    });
  };

  const updateUserTenantData = (
    key: keyof USERTENANT,
    value: string | boolean,
  ) => {
    setUserTenantData((prevState) => {
      if (prevState) {
        return {
          ...prevState,
          [key]: value,
        };
      }
      return prevState;
    });
  };

  const updateTenantDepartmentData = (
    key: keyof TENANTDEPARTMENT,
    value: string | boolean,
  ) => {
    setTenantDepartmentData((prevState) => {
      if (prevState) {
        return {
          ...prevState,
          [key]: value,
        };
      }
      return prevState;
    });
  };

  const updateError = (field, value) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: value,
    }));
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files!);
    setFiles(selectedFiles);
    dispatch(setFilesData(selectedFiles));

    const processImage = (file: File): Promise<string> => {
      return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const targetSize = 300;
        canvas.width = targetSize;
        canvas.height = targetSize;

        const img = new Image();
        img.onload = () => {
          const scale = Math.max(
            targetSize / img.width,
            targetSize / img.height,
          );
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;

          const offsetX = (scaledWidth - targetSize) / 2;
          const offsetY = (scaledHeight - targetSize) / 2;

          ctx!.clearRect(0, 0, targetSize, targetSize);
          ctx!.drawImage(img, -offsetX, -offsetY, scaledWidth, scaledHeight);

          resolve(canvas.toDataURL("image/jpeg", 1));
        };

        img.src = URL.createObjectURL(file);
      });
    };

    const processedPreviews = await Promise.all(
      selectedFiles.map((file) => processImage(file)),
    );
    setPreviews(processedPreviews);

    const fileName = selectedFiles.length > 0 ? selectedFiles[0].name : "";

    if (fileName !== userData?.profile_image_url) {
      setImageChanged(true);
    }
  };

  const handleFileDelete = () => {
    setPreviews([]);
    setDoImageDelete(true);
  };

  const handleDropdownChange = (e) => {
    setRoleError("");
    const selectedRoleId = e.target.value;
    const selectedRole = roles.find(
      (role) => role.id.toString() === selectedRoleId,
    );

    if (selectedRole?.id != null) {
      setCurrentUserRole(selectedRole);
      if (selectedRole.name.includes(ROLES.CONTACT)) {
        updateUserData("user_type", USER_TYPE.INTERNAL);
      } else {
        updateUserData("user_type", USER_TYPE.KEYCLOAK_USER);
      }
    }
  };

  const handleSelectDepartment = (group) => {
    if (!tenantDepartmentData) {
      setTenantDepartmentData(
        initTenantDepartmentData || BlankTenantDepartmentData,
      );
    }
    updateTenantDepartmentData("tenant_department_id", group.id);
    updateTenantDepartmentData("department_name", group.name);
    handleDepartmentModalClose();
  };

  const handleSelectBroadcastGroup = (group) => {
    setBroadcastGroup(group);
    handleBroadcastGroupModalClose();
  };

  const createNewUser = async (event) => {
    event.preventDefault();

    const fields: boolean = verifyFields();

    if (!fields) {
      errorToast(error_message.generic.bad_field);
      return;
    }

    handlePostUserData(event);
  };

  const processUserImage = async (id) => {
    await uploadUserImage(id, files);
  };

  const handlePostUserData = async (event, forceCreate: boolean = false) => {
    event.preventDefault();
    let response;
    const fields = verifyFields();

    if (!currentUserRole || currentUserRole.id === "") {
      setRoleError(UserFieldErrorMessages.roleError);
      setError(error_message.user.role);
      errorToast(UserFieldErrorMessages.roleError);
      return;
    }

    if (!fields) {
      errorToast(error_message.generic.bad_field);
      return;
    }

    if (userData.user_type === USER_TYPE.INTERNAL) {
      try {
        const response = await postContact(
          userData,
          setLoading,
          setError,
          forceCreate,
          userData.user_type,
          currentUserRole.id,
          tenantDepartmentData,
        );
        if (response.status === 200) {
          successToast(response.message);
          if (response.user_id) navigate(`/users/${response.user_id}`);
        } else {
          if (response.duplicate_users) {
            if (
              response.message.includes("User exists with same email") ||
              response.message.includes(
                "user with the same internal_email already exists in the tenant",
              )
            ) {
              const msg = TranslateErrorMessage(response.message);
              setError(msg);
              errorToast(msg);
            } else {
              if (response.duplicate_users) {
                setError("");
                setDuplicateUsers(response?.duplicate_users);
                setDuplicateExistsModal(true);
                setDupErrorType(translatedDuplicateMessages[response?.type]);
              } else {
                const msg = TranslateErrorMessage(response.message);
                setError(msg);
                errorToast(msg);
              }
            }
          }
        }
      } catch (err) {
        const msg = TranslateErrorMessage(err.message);
        setError(msg);
        errorToast(msg);
      } finally {
        setLoading(false);
      }
    } else {
      await postUserData(
        userData,
        tenantDepartmentData,
        broadcastGroup,
        keycloakGroups,
        currentUserRole,
        selectedApps,
        setLoading,
        setError,
        forceCreate,
      )
        .then((response) => {
          if (response.status == 200) {
            if (files.length >= 1) {
              processUserImage(response.user_id);
            }
            successToast(response.message);
            if (response.user_id) {
              navigate(`/users/${response.user_id}`);
            }
          } else {
            if (
              response.message.includes("User exists with same email") ||
              response.message.includes(
                "user with the same internal_email already exists in the tenant",
              )
            ) {
              const msg = TranslateErrorMessage(response.message);
              setError(msg);
              errorToast(msg);
            } else {
              if (response.duplicate_users) {
                setError("");
                setDuplicateUsers(response?.duplicate_users);
                setDuplicateExistsModal(true);
                setDupErrorType(translatedDuplicateMessages[response?.type]);
              } else {
                const msg = TranslateErrorMessage(response?.message);
                setError(msg);
                errorToast(msg);
              }
            }
          }
        })
        .catch(() => {
          const errorMessage = TranslateErrorMessage(response?.message);
          console.error(errorMessage);
          setError(errorMessage);
          errorToast(errorMessage);
        });
      setLoading(false);
    }
  };

  const handleUpdateContact = async (
    event,
    userId: string | undefined = id,
    forceCreate: boolean = true,
  ) => {
    setError("");
    event.preventDefault();
    const fields = verifyFields();
    setLoading(true);

    if (!fields) {
      errorToast(error_message.generic.bad_field);
      return;
    }

    if (!currentUserRole || currentUserRole.id === "") {
      setRoleError(error_message.user.role);
      setError(error_message.user.role);
      errorToast(roleError);
      return;
    }

    try {
      const response = await putUserData(
        userData,
        tenantDepartmentData,
        broadcastGroup,
        currentUserRole,
        selectedApps,
        keycloakGroups,
        setLoading,
        setError,
        userId,
      );
      if (response.status === 200) {
        if (response.status == 200) {
          if (imageChange) {
            processUserImage(userData.id);
          } else if (doImageDelete) {
            deleteUserImage(userData?.id, setLoading, setError);
          }
          successToast(response.message);
        } else {
          const errorMessage = TranslateErrorMessage(response.message);
          console.error(errorMessage);
          setError(errorMessage);
          errorToast(errorMessage);
        }

        navigate(`/users/${userId}`);
      } else {
        const msg = TranslateErrorMessage(response.message);
        setError(msg);
        errorToast(msg);
      }
    } catch (err) {
      const msg = TranslateErrorMessage(err);
      setError(msg);
      errorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePut = async (event, userID = "") => {
    setError("");
    event.preventDefault();
    const fields = verifyFields();

    if (!fields) {
      errorToast(error_message.generic.bad_field);
      return;
    }

    if (!currentUserRole || currentUserRole.id === "") {
      setRoleError(error_message.user.role);
      setError(error_message.user.role);
      errorToast(roleError);
      return;
    }

    if (yobelyLicenseError) {
      setError(error_message.yobely.license_error);
      errorToast(error_message.yobely.license_error);
      return;
    }

    putUserData(
      userData,
      tenantDepartmentData,
      broadcastGroup,
      currentUserRole,
      selectedApps,
      keycloakGroups,
      setLoading,
      setError,
      userID,
    )
      .then((response) => {
        if (response.status == 200) {
          if (imageChange) {
            processUserImage(userData.id);
          } else if (doImageDelete) {
            deleteUserImage(userData?.id, setLoading, setError);
          }

          successToast(response.message);
          if (id === "createUser") {
            navigate(`/users/${userID}`);
          }
        } else {
          if (response.duplicate_users) {
            const errorMessage = TranslateErrorMessage(response.message);
            if (
              errorMessage.includes("User exists with same email") ||
              errorMessage.includes("same email already exists in the tenant")
            ) {
              const msg = TranslateErrorMessage(response.message);
              setError(msg);
              errorToast(msg);
            } else {
              setError("");
              setDuplicateUsers(response.duplicate_users);
              setDuplicateExistsModal(true);
              setDupErrorType(translatedDuplicateMessages[response.type]);
            }
          } else {
            const errorMessage = TranslateErrorMessage(response.message);
            console.error(errorMessage);
            setError(errorMessage);
            errorToast(errorMessage);
          }
        }
      })
      .catch((error) => {
        const errorMessage = TranslateErrorMessage(error.message);
        console.error(errorMessage);
        setError(errorMessage);
        errorToast(errorMessage);
      });
  };

  useEffect(() => {
    if (!Array.isArray(userApps) || !Array.isArray(apps)) {
      return;
    }

    const updatedSelectedApps = apps?.filter((app) => {
      const isSelected = userApps?.some((userApp) => {
        return userApp?.app_id === app.id || userApp?.id === app.id;
      });

      return isSelected;
    });

    setSelectedApps(updatedSelectedApps);
  }, [userApps, apps]);

  const handleCheckboxChange = useCallback(
    (selectedApp: APP) => {
      setSelectedApps((prevSelectedApps) => {
        if (prevSelectedApps.some((app) => app.id === selectedApp.id)) {
          return prevSelectedApps.filter((app) => app.id !== selectedApp.id);
        } else {
          return [...prevSelectedApps, selectedApp];
        }
      });
    },
    [selectedApps],
  );

  const handleKeycloakGroupsCheckboxChange = useCallback(
    (selectedGroup) => {
      setKeycloakGroups((prevKeycloakGroups) => {
        if (prevKeycloakGroups.some((group) => group.id === selectedGroup.id)) {
          return prevKeycloakGroups.filter(
            (app) => app.id !== selectedGroup.id,
          );
        } else {
          return [...prevKeycloakGroups, selectedGroup];
        }
      });
    },
    [keycloakGroups],
  );

  const submitPasswordReset = async (password) => {
    setError("");
    await resetUserPassword(password, setLoading, setError, userData?.id)
      .then((response) => {
        if (response.status == 200) {
          successToast(response.message);
        } else {
          const responseMessage = TranslateErrorMessage(response);
          setError(responseMessage);
          errorToast(responseMessage);
        }
      })
      .catch((error) => {
        setError(TranslateErrorMessage(error.message));
        errorToast(TranslateErrorMessage(error.message));
      });
  };

  const handleUpdateDuplicateUser = async (
    event,
    userId: string | undefined = id,
    forceCreate: boolean = true,
  ) => {
    event.preventDefault();
    if (userData.user_type === USER_TYPE.INTERNAL) {
      try {
        await handleUpdateContact(event, userId, forceCreate);
      } catch (err) {
        const msg = TranslateErrorMessage(err.message);
        setError(msg);
        errorToast(msg);
      }
    } else {
      try {
        await handlePut(event, userId);
      } catch (err) {
        const msg = TranslateErrorMessage(err.message);
        setError(msg);
        errorToast(msg);
      }
    }
  };

  useEffect(() => {
    if (selectedApps?.length > 0) {
      const appNames = selectedApps.map((app) => app.name.toUpperCase());
      setHasYobelyLicense(appNames.includes(APP_NAMES.YOBELY));
      setHasKakelyLicense(appNames.includes(APP_NAMES.KAKELY));
    } else {
      setHasYobelyLicense(false);
      setHasKakelyLicense(false);
    }
  }, [selectedApps]);

  const verifyFields = () => {
    return !Object.values(errors).some((error) => error === true);
  };

  const handleImageLoaded = () => {
    setImageLoading(false);
  };

  const updateFormValues = () => {
    dispatch(setUserFormData(userData));
    dispatch(setTempUserId(id));
    dispatch(setTenantDepartmentFormData(tenantDepartmentData));
    dispatch(setUserTenantDepartmentData(userTenantData));
    dispatch(setSelectedAppsData(selectedApps));
    dispatch(setSelectedRoleData(currentUserRole));
    dispatch(setImagePreviewData(previews));
    dispatch(setBroadcastGroupData(broadcastGroup));
  };

  const handleDepartmentClick = () => {
    updateFormValues();
    setDepartmentModalOpen(true);
  };

  const handleDepartmentModalClose = () => {
    setDepartmentModalOpen(false);
  };

  const handleBroadcastGroupModalClick = () => {
    updateFormValues();
    setBroadcastGroupModalOpen(true);
  };

  const handleBroadcastGroupModalClose = () => {
    setBroadcastGroupModalOpen(false);
  };

  const handleOpenPasswordModal = () => {
    setPasswordChangeModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setPasswordChangeModalOpen(false);
  };

  const handleOpenWarningModal = () => {
    setWarningModalOpen(true);
  };

  const handleCloseWarningModal = () => {
    setWarningModalOpen(false);
  };

  return {
    userData,
    userTenantData,
    tenantDepartmentData,
    roles,
    contactRoleSelected,
    userRole,
    userNotFound,
    broadcastGroup,
    apps,
    userApps,
    currentUserRole,
    selectedApps,
    keycloakGroups,
    roleError,
    error,
    errors,
    loading,
    createUser,
    myUserPage,
    isAdminLoggedIn,
    hasYobelyLicense,
    hasKakelyLicense,
    adminRoleSelected,
    password,
    showPassword,
    imageLoading,
    files,
    previews,
    imageChange,
    doImageDelete,
    departmentModalOpen,
    broadcastGroupModalOpen,
    passwordChangeModal,
    warningModalOpen,
    departmentModalTitle,
    duplicateUsers,
    duplicateExistsModal,
    dupErrorType,
    navigate,
    setDuplicateExistsModal,
    handlePostUserData,
    updateTenantDepartmentData,
    submitPasswordReset,
    updateUserData,
    updateUserTenantData,
    updateError,
    verifyFields,
    createNewUser,
    handlePut,
    handleDropdownChange,
    handleFileDelete,
    handleOpenWarningModal,
    handleFileChange,
    handleImageLoaded,
    handleOpenPasswordModal,
    handleClosePasswordModal,
    handleBroadcastGroupModalClick,
    handleBroadcastGroupModalClose,
    handleSelectBroadcastGroup,
    handleCloseWarningModal,
    handleDepartmentModalClose,
    handleSelectDepartment,
    handleDepartmentClick,
    handleCheckboxChange,
    handleKeycloakGroupsCheckboxChange,
    setShowPassword,
    updatePassword,
    setPassword,
    setDepartmentModalOpen,
    setBroadcastGroupModalOpen,
    setPasswordChangeModalOpen,
    setWarningModalOpen,
    setImageLoading,
    setFiles,
    setPreviews,
    setImageChanged,
    setDoImageDelete,
    handleUpdateContact,
    handleUpdateDuplicateUser,
    fileInput,
  };
};
